
/**
 * Camera Service
 * Singleton service for managing camera access and streams
 */

// Event types for the camera service
export type CameraStatus = 'inactive' | 'pending' | 'active' | 'error';
export type CameraEvent = 'statusChange' | 'frame' | 'error';

interface CameraServiceOptions {
  facingMode?: 'user' | 'environment';
  maxResolution?: boolean;
}

// Type for event listeners
type CameraEventListener = (data: any) => void;

class CameraService {
  private static instance: CameraService;
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private status: CameraStatus = 'inactive';
  private listeners: Map<CameraEvent, Set<CameraEventListener>> = new Map();
  private refCount = 0;
  private readonly REF_COUNT_LIMIT = 10; // Safety limit
  private startPromise: Promise<MediaStream> | null = null;
  private isLocked = false;
  private lockTimeout: ReturnType<typeof setTimeout> | null = null;
  
  private constructor() {
    // Private constructor for singleton pattern
    console.log('Camera Service: Initialized');
    
    // Initialize event listener maps
    this.listeners.set('statusChange', new Set());
    this.listeners.set('frame', new Set());
    this.listeners.set('error', new Set());
    
    // Cleanup on page unload to prevent memory leaks
    window.addEventListener('beforeunload', this.forceCleanupOnUnload);
  }

  public static getInstance(): CameraService {
    if (!CameraService.instance) {
      CameraService.instance = new CameraService();
    }
    return CameraService.instance;
  }

  /**
   * Start the camera with specified options
   * Added mutex lock to prevent concurrent starts
   */
  public async startCamera(options: CameraServiceOptions = {}): Promise<MediaStream> {
    // Prevent concurrent start operations
    if (this.isLocked) {
      console.log('Camera Service: Operation locked, waiting for previous operation to complete');
      await this.waitForLock();
    }
    
    this.setLock(true);
    
    try {
      if (this.startPromise) {
        // Return existing promise if camera is already starting
        return this.startPromise;
      }
      
      if (this.refCount >= this.REF_COUNT_LIMIT) {
        console.warn(`Camera Service: Reference count limit (${this.REF_COUNT_LIMIT}) reached, resetting to 1`);
        this.refCount = 0;
      }

      this.refCount++;
      console.log(`Camera Service: Starting camera (ref count: ${this.refCount})`);
      
      if (this.stream && this.status === 'active') {
        console.log('Camera Service: Reusing existing camera stream');
        return this.stream;
      }
      
      this.setStatus('pending');
      
      // Create a promise that will be resolved when the camera is ready
      this.startPromise = new Promise<MediaStream>((resolve, reject) => {
        const startCamera = async () => {
          try {
            const constraints: MediaStreamConstraints = {
              video: {
                facingMode: options.facingMode || 'environment',
                width: options.maxResolution ? { ideal: 1920 } : { ideal: 1280 },
                height: options.maxResolution ? { ideal: 1080 } : { ideal: 720 }
              },
              audio: false
            };
    
            // Use a timeout to prevent hanging getUserMedia calls
            const mediaStreamPromise = navigator.mediaDevices.getUserMedia(constraints);
            const timeoutPromise = new Promise<MediaStream>((_, reject) => {
              setTimeout(() => reject(new Error('Camera access timeout')), 10000);
            });
            
            this.stream = await Promise.race([mediaStreamPromise, timeoutPromise]);
            this.setStatus('active');
            resolve(this.stream);
          } catch (error) {
            this.setStatus('error');
            console.error('Camera Service: Error starting camera:', error);
            this.emit('error', error);
            reject(error);
          } finally {
            this.startPromise = null;
          }
        };
        
        // Start the camera asynchronously
        startCamera();
      });
      
      return this.startPromise;
    } finally {
      // Always release lock when done
      this.setLock(false);
    }
  }

  /**
   * Stop the camera stream with enhanced cleanup
   */
  public stopCamera(forceStop: boolean = false): void {
    this.refCount = Math.max(0, this.refCount - 1);
    
    if (forceStop) {
      this.refCount = 0;
    }
    
    console.log(`Camera Service: Stop requested (ref count: ${this.refCount})`);
    
    if (this.refCount > 0) {
      console.log('Camera Service: Camera still in use, not stopping');
      return;
    }

    this.stopFrameProcessing();
    
    if (this.stream) {
      const tracks = this.stream.getTracks();
      for (const track of tracks) {
        try {
          track.stop();
          console.log(`Camera Service: Stopped track: ${track.kind}`);
        } catch (error) {
          console.error(`Camera Service: Error stopping track ${track.kind}:`, error);
        }
      }
      this.stream = null;
    }

    // Don't try to manipulate the DOM directly - let React handle it
    this.videoElement = null;
    this.setStatus('inactive');
    console.log('Camera Service: Camera stopped');
  }
  
  /**
   * Attach camera stream to a video element (new method)
   */
  public attachToVideo(videoElement: HTMLVideoElement): void {
    if (!this.stream) {
      console.error('Camera Service: No active stream to attach');
      return;
    }
    
    try {
      console.log('Camera Service: Attaching stream to video element');
      // Store the video element reference
      this.videoElement = videoElement;
      
      // Set the srcObject property safely
      if (videoElement.srcObject !== this.stream) {
        videoElement.srcObject = this.stream;
      }
    } catch (error) {
      console.error('Camera Service: Error attaching to video:', error);
      this.emit('error', error);
    }
  }

  /**
   * Reset the camera service (new method)
   */
  public reset(): void {
    console.log('Camera Service: Resetting...');
    // Stop the camera first
    this.stopCamera(true);
    
    // Reset all state
    this.refCount = 0;
    this.stream = null;
    this.videoElement = null;
    this.setStatus('inactive');
    
    // Clear any queued promises or locks
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
      this.lockTimeout = null;
    }
    this.isLocked = false;
    this.startPromise = null;
    
    console.log('Camera Service: Reset complete');
  }

  /**
   * Force cleanup all resources on page unload
   */
  private forceCleanupOnUnload = () => {
    if (this.stream) {
      try {
        const tracks = this.stream.getTracks();
        for (const track of tracks) {
          try {
            track.stop();
          } catch (e) {
            // Ignore errors during page unload
          }
        }
        this.stream = null;
      } catch (e) {
        // Ignore errors during page unload
      }
    }
    
    this.videoElement = null;
    this.refCount = 0;
    this.status = 'inactive';
  }

  /**
   * Set a mutex lock to prevent concurrent operations
   */
  private setLock(locked: boolean): void {
    this.isLocked = locked;
    
    // Clear any existing lock timeout
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
      this.lockTimeout = null;
    }
    
    // Set a safety timeout to ensure lock is eventually released
    if (locked) {
      this.lockTimeout = setTimeout(() => {
        console.warn('Camera Service: Lock timeout expired, forcing release');
        this.isLocked = false;
      }, 5000); // 5 second safety timeout
    }
  }
  
  /**
   * Wait for any existing lock to be released
   */
  private async waitForLock(): Promise<void> {
    return new Promise(resolve => {
      const checkLock = () => {
        if (!this.isLocked) {
          resolve();
        } else {
          setTimeout(checkLock, 100); // Poll every 100ms
        }
      };
      checkLock();
    });
  }

  /**
   * Set the current camera status and trigger event
   */
  private setStatus(status: CameraStatus): void {
    this.status = status;
    this.emit('statusChange', status);
  }

  /**
   * Event subscription methods
   */
  public on(event: CameraEvent, listener: CameraEventListener): () => void {
    const listeners = this.listeners.get(event) || new Set();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        listeners.delete(listener);
      }
    };
  }
  
  public off(event: CameraEvent, listener: CameraEventListener): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Emit an event to all registered listeners
   */
  private emit(event: CameraEvent, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error('Camera Service: Error in event listener:', error);
        }
      });
    }
  }

  /**
   * Stop frame processing
   */
  private stopFrameProcessing(): void {
    // No-op for now, will be implemented if needed
  }

  /**
   * Capture a still image from the current video stream
   */
  public async captureStillImage(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.isActive() || !this.stream) {
        reject(new Error('Camera not active'));
        return;
      }
      
      try {
        // Create a temporary video element if we don't have one
        const videoElement = this.videoElement || document.createElement('video');
        if (!this.videoElement) {
          videoElement.srcObject = this.stream;
          videoElement.play().catch(err => reject(err));
        }
        
        // Create a canvas to capture the frame
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not create canvas context'));
          return;
        }
        
        // Set canvas size to match video dimensions
        canvas.width = videoElement.videoWidth || 1280;
        canvas.height = videoElement.videoHeight || 720;
        
        // Draw the current video frame to the canvas
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        
        // Add a timeout for blob conversion to prevent hanging
        const timeoutId = setTimeout(() => {
          reject(new Error('Capture timeout'));
        }, 3000);
        
        // Convert the canvas to a blob
        canvas.toBlob(
          blob => {
            clearTimeout(timeoutId);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to capture image'));
            }
          },
          'image/jpeg',
          0.95
        );
      } catch (error) {
        reject(error);
      }
    });
  }
  
  /**
   * Check if camera is currently active
   */
  public isActive(): boolean {
    return this.status === 'active' && !!this.stream;
  }
  
  /**
   * Clean up resources when service is no longer needed
   */
  public destroy(): void {
    window.removeEventListener('beforeunload', this.forceCleanupOnUnload);
    this.stopCamera(true);
    this.listeners.clear();
  }
}

// Export a singleton instance
export const cameraService = CameraService.getInstance();
